import type { CohortAnnouncement } from '@efdigital/ll-xceed-v2/data/builders/xceed/messages';
import {
  markAnnouncementsSeen,
  unreadAnnouncementCount,
  unreadAnnouncements,
} from '@efdigital/ll-xceed-v2/utils/coaching/announcements';
import {
  cohortScopeKey,
  parseCohortScopeKey,
} from '@efdigital/ll-xceed-v2/utils/coaching/scope-key';
import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';

export default class CohortAnnouncementsService extends Service {
  @tracked announcements: CohortAnnouncement[] = [];
  @tracked scopeKey: string | null = null;
  /** Bumped when announcement data or seen state changes so nav badges re-render. */
  @tracked version = 0;

  get unreadCount(): number {
    void this.version;

    if (!this.scopeKey || !this.announcements.length) {
      return 0;
    }

    const parsed = parseCohortScopeKey(this.scopeKey);
    if (!parsed) {
      return 0;
    }

    return unreadAnnouncementCount(
      parsed.programId,
      parsed.cohortId,
      this.announcements
    );
  }

  /** Most recent announcement the coachee has not opened yet. */
  get latestUnread(): CohortAnnouncement | null {
    void this.version;

    if (!this.scopeKey || !this.announcements.length) {
      return null;
    }

    const parsed = parseCohortScopeKey(this.scopeKey);
    if (!parsed) {
      return null;
    }

    return (
      unreadAnnouncements(
        parsed.programId,
        parsed.cohortId,
        this.announcements
      )[0] ?? null
    );
  }

  sync(
    announcements: CohortAnnouncement[],
    programId: string,
    cohortId: string
  ): void {
    this.announcements = announcements;
    this.scopeKey = cohortScopeKey(programId, cohortId);
    this.version++;
  }

  markAllSeen(programId: string, cohortId: string): void {
    if (!this.announcements.length) {
      return;
    }

    markAnnouncementsSeen(
      programId,
      cohortId,
      this.announcements.map((announcement) => announcement.id)
    );
    this.version++;
  }
}

declare module '@ember/service' {
  interface Registry {
    'cohort-announcements': CohortAnnouncementsService;
  }
}
